import {Component, Input} from '@angular/core';
import {FormControl, ReactiveFormsModule} from '@angular/forms';

import {FieldConfig} from '../../../models/form-config.model';
import {SvgIconComponent} from '../../../../../widgets/svg-icon/svg-icon.component';

/** Testi usati quando la config non li specifica. */
const DEFAULT_STATUS = 'Impostata';
const DEFAULT_EDITING = 'Inserisci il nuovo valore nei campi qui sotto';
const DEFAULT_ACTION = 'Modifica';
const DEFAULT_CANCEL = 'Annulla';

/**
 * SecretStateField — dichiara che un segreto (password, PIN) è già registrato
 * e fa da interruttore per sostituirlo.
 *
 * Il FormControl contiene un booleano: `false` = "tieni quello registrato",
 * `true` = "sto inserendo un valore nuovo". I campi del segreto e della sua
 * conferma stanno accanto alla card con uno `showIf` che legge questo stesso
 * valore, quindi finché è `false` non vengono resi affatto — e un campo che non
 * esiste non può essere riempito per sbaglio.
 *
 * Il pulsante è UNO SOLO nei due stati: cambia etichetta e stile, non elemento.
 * Alternarne due con `@if` distruggerebbe quello appena premuto, e il focus
 * finirebbe sul body lasciando chi naviga da tastiera o con screen reader senza
 * un punto di ripresa.
 *
 * I pallini sono decorativi e di lunghezza fissa — convenzione diffusa per dire
 * "qui c'è un segreto". Il backend non restituisce né il valore né la sua
 * lunghezza, e pubblicarla non sarebbe comunque una buona idea. Sono
 * `aria-hidden` perché lette a voce sarebbero otto "punto" di fila.
 */
@Component({
  selector: 'app-secret-state-field',
  standalone: true,
  imports: [ReactiveFormsModule, SvgIconComponent],
  templateUrl: './secret-state-field.component.html',
  styleUrl: './secret-state-field.component.scss',
})
export class SecretStateFieldComponent {
  @Input({required: true}) field!: FieldConfig;
  @Input({required: true}) control!: FormControl;

  /** True mentre l'utente sta inserendo il valore nuovo. */
  get isEditing(): boolean {
    return this.control?.value === true;
  }

  get statusText(): string {
    const testi = this.field?.secretState;
    return this.isEditing
      ? testi?.editingText ?? DEFAULT_EDITING
      : testi?.statusText ?? DEFAULT_STATUS;
  }

  get buttonLabel(): string {
    const testi = this.field?.secretState;
    return this.isEditing
      ? testi?.cancelLabel ?? DEFAULT_CANCEL
      : testi?.actionLabel ?? DEFAULT_ACTION;
  }

  /**
   * Il pulsante porta il nome del segreto: in una pagina con tre card
   * ("Password", "Password Alternativa", "PIN") tre pulsanti letti solo
   * "Modifica" sarebbero indistinguibili fuori dal contesto visivo.
   */
  get buttonAriaLabel(): string {
    return this.isEditing
      ? `Annulla la modifica: ${this.field?.label ?? ''}`
      : `Modifica: ${this.field?.label ?? ''}`;
  }

  /**
   * Alterna lo stato. Il `setValue` emette di proposito: è l'evento che fa
   * ricalcolare all'engine visibilità e validatori dei campi del segreto, e
   * che — tornando a `false` — ne innesca lo svuotamento negli `sideEffects`
   * della categoria.
   */
  toggle(): void {
    this.control.setValue(!this.isEditing);
    this.control.markAsTouched();
  }
}
